/**
 * EntityLabel — single source of `{name} ({shortid})` composition
 * per UI_DISPLAY_CONVENTIONS.md R1.
 *
 * Use this on EVERY surface that needs to render a reference to a
 * platform entity (user, agent, org, tenant, workflow, skill, etc.).
 * Never compose the name + shortid inline at the call site; reach for
 * this component so the format stays consistent everywhere.
 *
 * R1 priority chain (handled internally):
 *   1. {name} ({shortid})              — name was resolved
 *   2. {kindFallback} ({shortid})      — name absent, render R3 graceful fallback
 *   3. —                                — neither id nor name (empty cell)
 *
 * Forensic short-id rendering matches the BE DisplayNameService.shortId():
 * first 8 hex chars + ellipsis. Full UUID surfaces via the `title`
 * (hover) attribute so operators can still grab it when they need it
 * for cross-system correlation — R1's "ID is the function" exception
 * is reachable without dedicating screen real estate to the full UUID.
 *
 * Props:
 *   id          UUID string (required for forensic short-id; if absent we render '—')
 *   name        Resolved entity name (optional; falls back to kindFallback)
 *   kind        Lower-case entity kind for the R3 fallback ('user', 'agent',
 *               'org', 'tenant', 'workflow', 'skill', 'peer', ...). Used
 *               only when `name` is absent; never visible otherwise.
 *   size        'sm' | 'md' | 'lg' — typography scale. Default 'md'.
 *   tone        'default' | 'muted' | 'inverted' — colour ramp. Default 'default'.
 *   onClick     Optional click handler; when present the label renders as a
 *               focus-ring-bearing <button> instead of a <span>. Use this
 *               for "click the parent agent to navigate to its detail page"
 *               affordances; leave undefined for read-only references.
 *   linkTo      Convenience: a URL string. If present, EntityLabel renders
 *               as a Link via React Router. Prefer this over onClick when
 *               you have a stable href.
 *   className   Extra Tailwind classes appended to the root element.
 *
 * Examples:
 *   <EntityLabel kind="user"  id={u.id} name={u.name} />
 *   <EntityLabel kind="agent" id={a.id} name={a.name} linkTo={`/agents/${a.id}`} />
 *   <EntityLabel kind="org"   id={r.orgId} name={orgMap[r.orgId]} size="sm" tone="muted" />
 */
import React from 'react';
import { Link } from 'react-router-dom';

const SHORT_ID_CHARS = 8;
const ELLIPSIS = '…';

function shortId(id) {
  if (!id) return '';
  const s = String(id);
  return s.length <= SHORT_ID_CHARS ? s : s.slice(0, SHORT_ID_CHARS) + ELLIPSIS;
}

const SIZE_STYLES = {
  sm: { name: 'text-[11px]',    id: 'text-[10px]'    },
  md: { name: 'text-[12.5px]',  id: 'text-[10.5px]'  },
  lg: { name: 'text-[13.5px]',  id: 'text-[11px]'    },
};

const TONE_STYLES = {
  default:  { name: 'text-v2-ink',           id: 'text-v2-ink-muted' },
  muted:    { name: 'text-v2-ink-secondary', id: 'text-v2-ink-muted' },
  inverted: { name: 'text-white',            id: 'text-white/70'     },
};

export default function EntityLabel({
  id,
  name,
  kind = 'entity',
  size = 'md',
  tone = 'default',
  onClick,
  linkTo,
  className = '',
}) {
  if (!id && !name) {
    return <span className={`text-v2-ink-muted ${className}`}>—</span>;
  }

  const sz = SIZE_STYLES[size] || SIZE_STYLES.md;
  const tn = TONE_STYLES[tone] || TONE_STYLES.default;

  // R3 fallback: resolved name preferred; otherwise show the lower-case
  // kind so the user sees what KIND of entity the id refers to instead
  // of a bare 36-char UUID. Never blank.
  const display = (name && String(name).trim()) ? String(name).trim() : kind;

  const inner = (
    <>
      <span className={`${sz.name} ${tn.name} font-medium`}>{display}</span>
      {id && (
        <>
          {' '}
          <code className={`${sz.id} ${tn.id} font-mono-v2`}>({shortId(id)})</code>
        </>
      )}
    </>
  );

  const rootClass = `inline-flex items-baseline gap-1 ${className}`.trim();
  // Full UUID on hover for the R1 "ID is the function" exception — operator
  // can still copy the full id when they need it without us dedicating
  // pixels to the 36-char string.
  const titleAttr = id || undefined;

  if (linkTo) {
    return (
      <Link to={linkTo} title={titleAttr} className={`${rootClass} v2-focus-ring hover:underline text-v2-action`}>
        {inner}
      </Link>
    );
  }
  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        title={titleAttr}
        className={`${rootClass} v2-focus-ring hover:underline text-v2-action bg-transparent border-0 p-0 cursor-pointer`}
      >
        {inner}
      </button>
    );
  }
  return (
    <span title={titleAttr} className={rootClass}>
      {inner}
    </span>
  );
}
